import type { IStore } from "@snackpass/snackpass-types";

import { getSidebarSections } from "#navigation/sidebar/sidebar-hierarchy";
import { Routes } from "#navigation/routes";
import { getRouteItems } from "#navigation/route-data";

import type { RouteItem } from "../types";

export function getExcludedSidebarRoutes(
    store: (IStore & { pandaDocDocumentId?: unknown }) | null,
    isSnackpassEmployee = false,
): Routes[] {
    const documentId = store?.pandaDocDocumentId;
    return isSnackpassEmployee ||
        (typeof documentId === "string" && documentId.trim())
        ? []
        : [Routes.Contract];
}

export function isUrlActive(currentPath: string, itemPath: string): boolean {
    if (itemPath === Routes.Index) {
        return currentPath === Routes.Index;
    }
    if (!itemPath) return false;

    // Remove trailing slashes for consistent comparison
    const normalizedCurrentPath = currentPath.replace(/\/+$/, "");
    const normalizedItemPath = itemPath.replace(/\/+$/, "");

    // Special case for root path
    if (normalizedItemPath === "/") {
        return normalizedCurrentPath === "/";
    }

    // Check if paths match exactly or if current path starts with item path
    // and the next character is a forward slash (to prevent partial matches)
    return (
        normalizedCurrentPath === normalizedItemPath ||
        (normalizedCurrentPath.startsWith(normalizedItemPath) &&
            normalizedCurrentPath[normalizedItemPath.length] === "/")
    );
}

export function hasActiveNestedRoute(
    items: RouteItem[] | undefined,
    pathname: string,
): boolean {
    if (!items) return false;

    return items.some((item) => {
        // Check if current item's URL is active
        const isCurrentItemActive = isUrlActive(pathname, item.url || "");

        // Recursively check nested items
        const areNestedItemsActive = item.items
            ? hasActiveNestedRoute(item.items, pathname)
            : false;

        return isCurrentItemActive || areNestedItemsActive;
    });
}

// Function to get all sidebar top level items
export function getAllSidebarTopLevelItems(): RouteItem[] {
    const sidebarSections = getSidebarSections();
    return sidebarSections.flatMap((section) => section.items);
}

// Function to get route to breadcrumb map
export function getRouteToBreadcrumbMap(): Map<Routes, RouteItem[]> {
    const map = new Map<Routes, RouteItem[]>();

    function buildBreadcrumbMap(
        items: RouteItem[],
        currentBreadcrumb: RouteItem[] = [],
    ) {
        for (const item of items) {
            const breadcrumb = [...currentBreadcrumb, item];

            if (item.url) {
                map.set(item.url, breadcrumb);

                // Handle nonSidebarSubpages
                if (item.nonSidebarSubpages) {
                    const routeItems: Partial<Record<Routes, RouteItem>> =
                        getRouteItems();
                    for (const subpage of item.nonSidebarSubpages) {
                        const subpageItem = routeItems[subpage];
                        if (subpageItem) {
                            map.set(subpage, [...breadcrumb, subpageItem]);
                        }
                    }
                }
            }

            if (item.items) {
                buildBreadcrumbMap(item.items, breadcrumb);
            }
        }
    }

    const sidebarSections = getSidebarSections();
    buildBreadcrumbMap(sidebarSections.flatMap((section) => section.items));
    return map;
}

export function getBreadcrumbReadable(route: Routes): string {
    const routeToBreadcrumbMap = getRouteToBreadcrumbMap();
    return (
        routeToBreadcrumbMap
            .get(route)
            ?.map((item) => String(item.title))
            .join("/") || ""
    );
}
